import { useState, useEffect, useCallback } from "react";
import { IHotel } from "@vmosolutions/oc-interfaces";
import { FormikSelectField } from "src/components/FormikComponents/FormikWrappers";
import { ISelectFieldProps } from "src/components/FormikComponents/SelectField";
// API Calls
import AmplifyAPIWrapper from "src/API/AmplifyAPIWrapper";
import { formatFunction, parseFunction } from "./helpers";
import { groupedHotelList } from "src/pages/CrewTravel/TabHotels/ManageHotels";

interface IHotelSelectFieldProps extends Omit<ISelectFieldProps, "options"> {
  mosaicPane: HTMLElement;
  valueKey?: string; // valueKey sent back as value - use entire object otherwise.
  showInactive?: boolean;
}

/**
 * Sepcialized wrapper for "built-in" oc hotel select field.
 * @param props -
 */
export const HotelSelectField = (props: IHotelSelectFieldProps): JSX.Element => {
  const { mosaicPane, label, name, onChange, valueKey, isMulti, defaultValue, ...selectProps } = props;

  ///////////////////////////////////////
  // MANAGE HOTEL LIST
  ///////////////////////////////////////
  const [hotels, setHotels] = useState<IHotel[]>([]);

  useEffect(() => {
    const getHotels = async (): Promise<void> => {
      //const notifications: INotifications = { dispatch, errorMessage: "No Aircraft Groups found." };
      const response = await AmplifyAPIWrapper.Get<IHotel[]>("/hotels");
      if (Array.isArray(response)) setHotels(response);
    };
    getHotels();
  }, []);

  // Setup Format And Parse functions (used if valueKey is passed)
  const formatValue = useCallback(
    (value: string) => formatFunction(value, hotels, valueKey, isMulti),
    [hotels, valueKey, isMulti]
  );
  const parseOption = useCallback((option: string) => parseFunction(option, valueKey, isMulti), [valueKey, isMulti]);

  return (
    <FormikSelectField
      name={name}
      label={label ?? "Hotel Selector"}
      isMulti={isMulti}
      options={groupedHotelList(hotels)}
      getOptionLabel={(option: IHotel) => `${option.icao} - ${option.priority} - ${option.name}`}
      getOptionValue={(option: IHotel) => option?._id}
      format={(value) => (valueKey ? formatValue(value) : value)}
      parse={(option) => (valueKey ? parseOption(option) : option)}
      onChange={(value: any) => !!onChange && onChange(value)}
      menuPortalTarget={mosaicPane}
      menuPosition={mosaicPane ? "fixed" : undefined}
      isSearchable={true}
      openMenuOnFocus={false}
      isClearable={true}
      placeholder={`Select Hotel${isMulti && "s"}`}
      defaultValue={defaultValue}
      {...selectProps} // override with any props passed in such as isMulti, format, parse.
    />
  );
};
